import htmx from "htmx.org";
// First, so a module that fails to load cannot take dialogs down with it.
import "./modal_focus.js";
import { initRichText } from "./rich_text.js";
import { initAcademyDocumentUpload } from "./academy_document_upload.js";
import { initAcademyAttachmentsForm } from "./academy_attachments_upload.js";
import { initAttachmentPicker } from "./attachment_picker.js";
import { initMessageTemplates } from "./message_template.js";
import { initImportGrid } from "./import_grid.js";
import { initImportMapping, initImportReview } from "./import_mapping.js";
import { initImportUpload } from "./import_upload.js";
import { initCadenceToggle } from "./cadence_toggle.js";
import { initInputFormat } from "./input_format.js";
import { initAudienceToggle } from "./audience_toggle.js";
import { initCampaignAudience } from "./campaign_audience.js";
import { initCampaignBackup } from "./campaign_backup.js";
import { initCampaignChannel } from "./campaign_channel.js";
import { initMessageEditor } from "./message_editor.js";
import { initPlaceholderChips } from "./placeholder_chips.js";
import { initOutreachSettings } from "./outreach_settings.js";
import { initCampaignStepKind } from "./campaign_step_kind.js";
import { initSMSThread } from "./sms_thread.js";
import { initKanban } from "./kanban.js";
import { initMessageChannel } from "./message_channel.js";
import { initCountUp } from "./count_up.js";
import { initPasswordStrength } from "./password_strength.js";
import { initPresentationUpload } from "./presentation_upload.js";
import { initPresentationCreate } from "./presentation_create.js";
import { initPresentationRetry } from "./presentation_retry.js";
import { initPresentationDocuments } from "./presentation_documents.js";
import { initAcademyLessonModal } from "./academy_lesson_create.js";
import { initAcademyCourseCreate } from "./academy_course_create.js";
import { initAcademyEdit } from "./academy_edit.js";
import { initAcademyVideoReplace } from "./academy_video_replace.js";
import { initProfileAvatar } from "./profile_avatar.js";
import { initSMSOptInProofUpload } from "./sms_optin_proof_upload.js";
import { initSEOImage } from "./seo_image_upload.js";
import { initAccountChangeModal } from "./account_change_modal.js";
import { initAccountActions } from "./account_actions.js";
import { initNotifications } from "./notifications.js";
import { initAdminGenerateSource } from "./admin_generate_source.js";
import { initAdminCourseForm } from "./admin_course_form.js";
import { initAssigneePopover } from "./assignee_popover.js";
import { initMessageRecipients } from "./message_recipients.js";
import { initBrandColor } from "./brand_color.js";
import { initBulkActions } from "./bulk_actions.js";
import { initNoticeDismiss } from "./notice_dismiss.js";
import { initBulkSelect } from "./bulk_select.js";
import { initWorkspaceLogo } from "./workspace_logo.js";
import { initEditorPopover } from "./editor_popover.js";
import { initRelativeTime } from "./relative_time.js";
import { initTimelineDays } from "./timeline_days.js";

window.htmx2 = htmx;

window.htmx2.config.responseHandling = [
  {
    code: "204",
    swap: false,
  },
  {
    code: "[23]..",
    swap: true,
  },
  {
    code: "[45]..",
    swap: true,
    error: true,
  },
];

window.htmx2.config.globalViewTransitions = true;

// A history restore swaps into the whole body, so it must fetch the full
// page. Sent as an htmx request, it would get the fragment meant for a
// list swap.
window.htmx2.config.historyRestoreAsHxRequest = false;

// Back fetches the page fresh instead of restoring htmx's snapshot of it.
// A snapshot brings back DOM that widgets built at runtime (upload rows,
// a count caught mid-way, a pruned select) without the state behind it,
// and a list as it stood before an edit.
window.htmx2.config.historyCacheSize = 0;

// Only a snapshot carries its scroll position, so each page remembers its
// own, and the fresh copy's swap scrolls back to it once it settles.
const SCROLL_KEY = "liferida-scroll:";
document.body.addEventListener("htmx:beforeHistorySave", (event) => {
  try {
    sessionStorage.setItem(SCROLL_KEY + event.detail.path, String(scrollY));
  } catch {
    // Storage can be off in a private window; Back then starts at the top.
  }
});

// A fresh copy that fails to load, at the network or with an error
// status, would leave the old page under the new address, so the browser
// loads that address itself.
const reloadPage = () => location.reload();
document.body.addEventListener("htmx:historyCacheMiss", (event) => {
  event.detail.xhr.addEventListener("error", reloadPage);

  try {
    const y = sessionStorage.getItem(SCROLL_KEY + event.detail.path);
    if (y !== null) event.detail.swapSpec.scroll = Number(y);
  } catch {
    // As above.
  }
});
document.body.addEventListener("htmx:historyCacheMissLoadError", reloadPage);

// The fetch follows redirects silently, so an expired session would swap
// the sign-in page in under the old address. A copy from anywhere but the
// address asked for gets a real load instead.
document.body.addEventListener("htmx:historyCacheMissLoad", (event) => {
  const { xhr, path } = event.detail;
  const landed = new URL(xhr.responseURL, location.origin);
  if (landed.pathname + landed.search !== path) reloadPage();
});

const me = (previous) =>
  previous
    ? window.document.currentScript?.previousElementSibling
    : window.document.currentScript?.parentElement;

window.me = me;

// Bootstrap progressive-enhancement widgets on initial load and after every
// htmx swap so newly inserted DOM still gets wired up.
const bootstrapWidgets = (root = document) => {
  initRichText(root);
  initAcademyDocumentUpload(root);
  initAcademyAttachmentsForm(root);
  initAttachmentPicker(root);
  initMessageTemplates(root);
  initImportGrid(root);
  initImportMapping(root);
  initImportReview(root);
  initCadenceToggle(root);
  initInputFormat(root);
  initAudienceToggle(root);
  initCampaignAudience(root);
  initCampaignBackup(root);
  initCampaignChannel(root);
  initMessageEditor(root);
  initPlaceholderChips(root);
  initCampaignStepKind(root);
  initSMSThread(root);
  initKanban(root);
  initMessageChannel(root);
  initCountUp(root);
  initPasswordStrength(root);
  initPresentationUpload(root);
  initPresentationCreate(root);
  initPresentationRetry(root);
  initPresentationDocuments(root);
  initAcademyLessonModal(root);
  initAcademyCourseCreate(root);
  initAcademyEdit(root);
  initAcademyVideoReplace(root);
  initProfileAvatar(root);
  initImportUpload(root);
  initSMSOptInProofUpload(root);
  initSEOImage(root);
  initAccountChangeModal(root);
  initAccountActions(root);
  initNotifications(root);
  initOutreachSettings(root);
  initAdminGenerateSource(root);
  initAdminCourseForm(root);
  initAssigneePopover();
  initMessageRecipients();
  initBrandColor(root);
  initBulkSelect(root);
  initBulkActions(root);
  initNoticeDismiss();
  initWorkspaceLogo(root);
  initEditorPopover(root);
  initRelativeTime(root);
  initTimelineDays(root);
};

document.addEventListener("DOMContentLoaded", () => bootstrapWidgets());
document.body.addEventListener("htmx:afterSwap", (event) =>
  bootstrapWidgets(event.target),
);
